import React from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Stack,
  Tag,
  Text,
} from "@chakra-ui/react";
import { FiArrowRight, FiCheck, FiMapPin, FiTruck } from "react-icons/fi";

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return `₹${Number.isFinite(amount) ? amount.toFixed(0) : "0"}`;
};

const formatDistance = (value) => {
  const amount = Number(value || 0);
  return `${Number.isFinite(amount) ? amount.toFixed(1) : "0.0"} km`;
};

const getAvailabilityTone = (pharmacy) => {
  if (pharmacy.allAvailable || Number(pharmacy.unavailableItems || 0) === 0) {
    return {
      bg: "#ecfdf5",
      border: "#bbf7d0",
      color: "#166534",
      label: "All requested items available",
    };
  }
  if (Number(pharmacy.availableItems || 0) > 0) {
    return {
      bg: "#fffbeb",
      border: "#fde68a",
      color: "#92400e",
      label: `${pharmacy.availableItems} available · ${pharmacy.unavailableItems || 0} pending`,
    };
  }
  return {
    bg: "#fff7ed",
    border: "#fed7aa",
    color: "#c2410c",
    label: pharmacy.isConnected === false
      ? "Store offline - prices shown are estimates"
      : pharmacy.availabilityError
        ? "Store responded, live stock unavailable"
        : "No requested items matched",
  };
};

const getMetricItems = (pharmacy) => {
  const metrics = [
    {
      label: "Match",
      value: `${Math.round(Number(pharmacy.matchPercent || 0))}%`,
    },
    {
      label: "Distance",
      value: formatDistance(pharmacy.distanceKm),
    },
  ];

  if (Number(pharmacy.totalAmount) > 0) {
    metrics.push({
      label: "Order total",
      value: formatCurrency(pharmacy.totalAmount),
    });
  }

  if (Number(pharmacy.savingsAmount) > 0) {
    metrics.push({
      label: "You save",
      value: formatCurrency(pharmacy.savingsAmount),
    });
  }

  return metrics;
};


const mergeWithRequestedItems = (pharmacyItems, requestedItems) => {
  // Unconnected store — no items at all, show nothing
  if (!Array.isArray(pharmacyItems) || pharmacyItems.length === 0) return [];

  const cartItems = Array.isArray(requestedItems) ? requestedItems : [];

  // Items are sent to the branch in cart order and returned in the same order.
  // Match by index: pharmacy.items[i] corresponds to cartItems[i].
  // Use the cart name as display name — ignore the store's internal item_name.
  const result = cartItems.map((ci, i) => {
    const storeItem = pharmacyItems[i];
    const cartName = ci.name || ci.productName || "Medicine";
    if (!storeItem) {
      // store didn't return a row for this index — not found
      return {
        name: cartName,
        requestedQty: Number(ci.quantity || 1),
        price: 0,
        mrp: 0,
        available: false,
      };
    }
    const notInDb = storeItem?.reason === "item_not_found";
    const storeName = storeItem?.item_name || storeItem?.name || storeItem?.product_name || "";
    return {
      name: !notInDb && storeName ? storeName : cartName,
      requestedQty: Number(storeItem?.requested_qty || ci.quantity || 1),
      price: Number(storeItem?.sale_rate || 0),
      mrp: Number(storeItem?.mrp || 0),
      available: storeItem?.is_available ?? (storeItem?.reason !== "item_not_found"),
    };
  });

  return result;
};

const OfferCard = ({ label, labelColor, labelBg, pharmacy, onChoose, isSelected, showBestDeal, requestedItems }) => {
  if (!pharmacy) return null;

  const availability = getAvailabilityTone(pharmacy);
  const metrics = getMetricItems(pharmacy);
  const lineItems = mergeWithRequestedItems(pharmacy.items, requestedItems).slice(0, 8);

  return (
    <Box
      bg={isSelected ? "#f0fdfa" : "white"}
      border={isSelected ? "1.5px solid #0f766e" : "1px solid #e7edf3"}
      borderRadius="12px"
      px={3}
      py={2.5}
      boxShadow="0 1px 4px rgba(15,23,42,0.05)"
      transition="all 0.15s ease"
    >
      {/* Row 1: label + name + choose button */}
      <Flex align="center" justify="space-between" gap={2} mb={1.5}>
        <Flex align="center" gap={2} minW={0} flex={1}>
          <Tag borderRadius="full" bg={labelBg} color={labelColor} fontSize="10px" fontWeight="800" px={2} py="2px" flexShrink={0}>
            {label}
          </Tag>
          <Text fontSize="13px" fontWeight="800" color="#0f172a" noOfLines={1} m={0} flex={1}>
            {pharmacy.name}
          </Text>
        </Flex>
        <Button
          size="xs"
          h="28px"
          px={3}
          bg={isSelected ? "#0f766e" : "white"}
          color={isSelected ? "white" : "#0f766e"}
          border="1.5px solid"
          borderColor={isSelected ? "#0f766e" : "#cbd5e1"}
          _hover={{ bg: isSelected ? "#0d9488" : "#f0fdfa", borderColor: "#0f766e" }}
          borderRadius="full"
          fontWeight="800"
          fontSize="11px"
          flexShrink={0}
          rightIcon={isSelected ? <FiCheck size={11} /> : <FiArrowRight size={11} />}
          onClick={() => onChoose(pharmacy)}
        >
          {isSelected ? "Selected" : "Choose"}
        </Button>
      </Flex>

      {/* Row 2: address + availability badge inline */}
      <Flex align="center" justify="space-between" gap={2} mb={2}>
        {pharmacy.address ? (
          <HStack spacing={1} color="#64748b" minW={0} flex={1}>
            <FiMapPin size={11} />
            <Text fontSize="11px" noOfLines={1} m={0}>{pharmacy.address}</Text>
          </HStack>
        ) : <Box flex={1} />}
        <Box
          bg={availability.bg}
          border={`1px solid ${availability.border}`}
          borderRadius="full"
          px={2}
          py="2px"
          flexShrink={0}
        >
          <Text fontSize="10px" fontWeight="700" color={availability.color} m={0} whiteSpace="nowrap">
            {availability.label}
          </Text>
        </Box>
      </Flex>

      {/* Row 2b: delivery message if own delivery enabled */}
      {pharmacy.delivery?.enabled && pharmacy.delivery?.message && (
        <Flex align="center" gap={1.5} mb={2} bg="#f0fdf4" px={2} py={1.5} borderRadius="8px">
          <FiTruck size={14} color="#16a34a" flexShrink={0} />
          <Text fontSize="11px" fontWeight="600" color="#166534" m={0}>
            {pharmacy.delivery.message}
          </Text>
        </Flex>
      )}

      {/* Row 3: metrics inline */}
      <HStack spacing={3} divider={<Box w="1px" h="18px" bg="#e7edf3" />} mb={lineItems.length ? 2 : 0} flexWrap="wrap">
        {metrics.map((metric) => (
          <Box key={metric.label}>
            <Text fontSize="9px" fontWeight="700" color="#94a3b8" textTransform="uppercase" letterSpacing="0.08em" m={0}>{metric.label}</Text>
            <Text fontSize="12px" fontWeight="800" color="#0f172a" m={0}>{metric.value}</Text>
          </Box>
        ))}
        {showBestDeal && (
          <Tag borderRadius="full" bg="#fff7ed" color="#c2410c" fontSize="10px" fontWeight="800" px={2} py="1px">Best deal</Tag>
        )}
      </HStack>

      {/* Row 4: item breakdown (collapsed) */}
      {lineItems.length > 0 && (
        <Box borderTop="1px solid #f1f5f9" pt={1.5}>
          <Stack spacing={1}>
            {lineItems.map((item, index) => {
              const lineTotal = item.price > 0 ? item.price * item.requestedQty : 0;
              const mrpTotal = item.mrp > 0 ? item.mrp * item.requestedQty : 0;
              const hasSaving = mrpTotal > lineTotal && lineTotal > 0;
              return (
                <Flex key={`${item.name}-${index}`} align="center" justify="space-between" gap={2}>
                  <Flex align="center" gap={1.5} minW={0} flex={1}>
                    <Box w="5px" h="5px" borderRadius="full" bg={item.available === null ? "#cbd5e1" : item.available ? "#22c55e" : "#ef4444"} flexShrink={0} />
                    <Text fontSize="11px" fontWeight="600" color="#334155" noOfLines={1} m={0}>
                      {item.name}
                      <Text as="span" color="#94a3b8" fontWeight="400"> ×{item.requestedQty}</Text>
                    </Text>
                  </Flex>
                  <HStack spacing={1} flexShrink={0}>
                    {hasSaving && (
                      <Text fontSize="10px" color="#94a3b8" textDecoration="line-through" m={0}>
                        {formatCurrency(mrpTotal)}
                      </Text>
                    )}
                    <Text fontSize="11px" fontWeight="700" color={lineTotal > 0 ? "#0f766e" : "#b91c1c"} m={0}>
                      {lineTotal > 0 ? formatCurrency(lineTotal) : "Store to confirm"}
                    </Text>
                  </HStack>
                </Flex>
              );
            })}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

const getPharmacyLabel = (pharmacy, index, bestMatchId, bestPriceId, nearestId) => {
  const id = pharmacy.pharmacyId || pharmacy.id || pharmacy.name;
  if (id === bestMatchId && id === bestPriceId) return { label: "Top match · Best price", labelColor: "#0f766e", labelBg: "#f0fdfa" };
  if (id === bestMatchId) return { label: "Top match", labelColor: "#0f766e", labelBg: "#f0fdfa" };
  if (id === bestPriceId) return { label: "Best price", labelColor: "#166534", labelBg: "#dcfce7" };
  if (id === nearestId) return { label: "Nearest", labelColor: "#1d4ed8", labelBg: "#dbeafe" };
  return { label: `#${index + 1}`, labelColor: "#475569", labelBg: "#f1f5f9" };
};

const PharmacyOfferCards = ({ pharmacies, onChoose, selectedPharmacyId, requestedItems }) => {
  if (!pharmacies?.length) return null;

  const byBestMatch = pharmacies.find((p) => p.isBestMatch) || pharmacies[0];
  const byPrice = [...pharmacies].sort((a, b) => Number(a.totalAmount || Number.MAX_SAFE_INTEGER) - Number(b.totalAmount || Number.MAX_SAFE_INTEGER))[0];
  const byDistance = [...pharmacies].sort((a, b) => Number(a.distanceKm || 0) - Number(b.distanceKm || 0))[0];

  const bestMatchId = byBestMatch?.pharmacyId || byBestMatch?.id || byBestMatch?.name;
  const bestPriceId = byPrice?.pharmacyId || byPrice?.id || byPrice?.name;
  const nearestId = byDistance?.pharmacyId || byDistance?.id || byDistance?.name;

  return (
    <Stack spacing={3}>
      {pharmacies.map((pharmacy, index) => {
        const cardId = pharmacy.pharmacyId || pharmacy.id || pharmacy.name;
        const { label, labelColor, labelBg } = getPharmacyLabel(pharmacy, index, bestMatchId, bestPriceId, nearestId);
        return (
          <OfferCard
            key={cardId}
            label={label}
            labelColor={labelColor}
            labelBg={labelBg}
            pharmacy={pharmacy}
            onChoose={onChoose}
            isSelected={selectedPharmacyId && cardId === selectedPharmacyId}
            showBestDeal={cardId === bestPriceId}
            requestedItems={requestedItems}
          />
        );
      })}
    </Stack>
  );
};

export default PharmacyOfferCards;
